import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

/* =========================================================================
 * User preferences — language, units, format toggles. Persisted to
 * localStorage and exposed via a React context so any component can
 * read/write without prop drilling.
 *
 * Defaults are sourced from the browser locale (navigator.language) so
 * a Hindi user opening for the first time sees Devanagari + their
 * country's default unit set.
 *
 * Phase 42 (Feb 18 2026).
 * ===================================================================== */

export const LANGUAGES = [
  { code: "en", label: "English", native: "English" },
  { code: "hi", label: "Hindi", native: "हिन्दी" },
  { code: "ta", label: "Tamil", native: "தமிழ்" },
  { code: "te", label: "Telugu", native: "తెలుగు" },
  { code: "mr", label: "Marathi", native: "मराठी" },
  { code: "bn", label: "Bengali", native: "বাংলা" },
  { code: "gu", label: "Gujarati", native: "ગુજરાતી" },
  { code: "kn", label: "Kannada", native: "ಕನ್ನಡ" },
  { code: "ml", label: "Malayalam", native: "മലയാളം" },
  { code: "pa", label: "Punjabi", native: "ਪੰਜਾਬੀ" },
  { code: "ja", label: "Japanese", native: "日本語" },
  { code: "zh", label: "Chinese", native: "中文" },
  { code: "es", label: "Spanish", native: "Español" },
  { code: "fr", label: "French", native: "Français" },
  { code: "de", label: "German", native: "Deutsch" },
  { code: "ar", label: "Arabic", native: "العربية" },
];

// Countries that default to imperial (Fahrenheit / mph)
const IMPERIAL_COUNTRIES = new Set(["US", "BS", "BZ", "KY", "PW", "FM", "MH", "LR"]);

function detectDefaults() {
  const lang = (typeof navigator !== "undefined" && navigator.language) || "en";
  const primary = lang.split("-")[0].toLowerCase();
  const region = (lang.split("-")[1] || "").toUpperCase();
  const supported = LANGUAGES.some((l) => l.code === primary) ? primary : "en";
  const imperial = IMPERIAL_COUNTRIES.has(region);
  return {
    language: supported,
    tempUnit: imperial ? "F" : "C",
    windUnit: imperial ? "mph" : "kmh",
    pressureUnit: imperial ? "inHg" : "hPa",
    distanceUnit: imperial ? "mi" : "km",
    timeFormat: imperial ? "12h" : "24h",
    haptics: true,
  };
}

const STORAGE_KEY = "nowcast.settings.v1";

function loadSettings() {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return { ...detectDefaults(), ...JSON.parse(stored) };
    }
  } catch (e) {
    // localStorage unavailable / corrupted → fall through to defaults
  }
  return detectDefaults();
}

function saveSettings(s) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
  } catch (e) {
    // ignore — user might have private mode or storage full
  }
}

const SettingsContext = createContext(null);

export function SettingsProvider({ children }) {
  const [settings, setSettings] = useState(() =>
    typeof window !== "undefined" ? loadSettings() : detectDefaults()
  );

  useEffect(() => {
    saveSettings(settings);
  }, [settings]);

  const update = useCallback((patch) => {
    setSettings((prev) => ({ ...prev, ...patch }));
  }, []);

  const reset = useCallback(() => {
    setSettings(detectDefaults());
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch (e) {
      // ignore
    }
  }, []);

  const value = useMemo(() => ({ settings, update, reset }), [settings, update, reset]);
  return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>;
}

export function useSettings() {
  const ctx = useContext(SettingsContext);
  if (!ctx) {
    // Defensive default — if somehow used outside the provider, fall back
    // to detected defaults instead of crashing.
    return {
      settings: detectDefaults(),
      update: () => {},
      reset: () => {},
    };
  }
  return ctx;
}
